import {useEffect, useRef, useState} from 'react';
import axios from 'axios';
import {messageCallbackType} from '@stomp/stompjs/src/types';
import {Client, IFrame} from '@stomp/stompjs';
import {StompHeaders} from '@stomp/stompjs/esm6/stomp-headers';
import SockJS from 'sockjs-client';

interface IUseWebsocket {
  isConnected: boolean
  isConnecting: boolean
  sendMessage: (msg: string) => void
  connect: (dialogueId: number, callback: messageCallbackType) => void
  disconnect: () => Promise<void>
}

export const useWebsocket = (): IUseWebsocket => {
  const connectedDialogueId = useRef<number>();
  const clientRef = useRef<Client>();
  const [client, setClient] = useState<Client>();
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const [isConnected, setIsConnected] = useState<boolean>(false);

  function activate(dialogueId: number, callback: messageCallbackType) {
    setIsConnecting(true);

    const stompClient = new Client({
      webSocketFactory: () => {
        return new SockJS(process.env.REACT_APP_WEBSOCKET_URL || '');
      },
      connectHeaders: {...axios.defaults.headers.common as StompHeaders},
      debug: (str: string) => {
        console.log(str);
      },
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000
    });

    stompClient.onConnect = (frame: IFrame) => {
      stompClient.subscribe(`/topic/dialogue/${dialogueId}/reply`, callback, {...axios.defaults.headers.common as StompHeaders});
      setIsConnecting(false);
      setIsConnected(true);
    };

    stompClient.onWebSocketClose = (frame: IFrame) => {
      setIsConnected(false);
    };

    stompClient.onStompError = (frame: IFrame) => {
      console.log('onStompError: ', frame);
    };

    stompClient.onWebSocketError = (frame: IFrame) => {
      console.log('onWebSocketError: ', frame);
      setIsConnected(false);
    };

    stompClient.activate();
    clientRef.current = stompClient;
    setClient(stompClient);
  }

  const connect = (dialogueId: number, callback: messageCallbackType) => {
    if (connectedDialogueId.current === dialogueId) {
      return;
    }
    connectedDialogueId.current = dialogueId;

    const previousClient = clientRef.current;

    if (previousClient) {
      // A different dialogueId was already connected (or connecting) -- tear it down first so
      // its connection isn't leaked alongside the new one (UCSF-3485), which is exactly the
      // orphaned-connection scenario UCSF-3448 documents causing proxy-level handshake rejection.
      clientRef.current = undefined;
      previousClient.deactivate().then(() => activate(dialogueId, callback));
    } else {
      activate(dialogueId, callback);
    }
  };

  async function disconnect() {
    connectedDialogueId.current = undefined;
    if (clientRef.current) {
      await clientRef.current.deactivate();
      clientRef.current = undefined;
    }
    setIsConnecting(false);
    setIsConnected(false);
  }

  useEffect(() => {
    return () => {
      clientRef.current?.deactivate();
    };
  }, []);

  function sendMessage(msg: string) {
    if (client && client.connected) {
      client.publish({
        destination: '/app/message',
        body: msg,
        skipContentLengthHeader: true,
        headers: {...axios.defaults.headers.common as StompHeaders}
      });
    } else {
      setIsConnected(false);
      console.log('Send error: Client is disconnected');
    }
  }

  return {
    isConnected,
    isConnecting,
    sendMessage,
    connect,
    disconnect
  };
};
